<script setup lang="ts">
const route = useRoute();
const to = route.query.to as string;
const params = { to: to };

useAuthGuardRedirection(params);

const { t } = useI18n();
const { logout } = useUser();
const router = useRouter();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

const accountMenuItems = ref([
  {
    label: t("account.menu.accountOverviewHeader"),
    route: "/account",
  },
  {
    label: t("account.yourProfile"),
    route: "/account/profile",
  },
  {
    label: t("account.yourAddress"),
    route: "/account/address",
  },
  {
    label: t("account.myPaymentsHeader"),
    route: "/account/payment",
  },
  {
    label: t("account.wishlistHeader"),
    route: "/account/wishlist",
  },
  {
    label: t("account.orderHistoryHeader"),
    route: "/account/order",
  },
  {
    label: t("form.logout"),
    icon: "pi pi-sign-out",
    command: async () => {
      await invokeLogout();
    },
  },
]);

async function invokeLogout() {
  logout();
  router.push("/");
}

const cartIsOpen = computed({
  get: () => route.query.sidebar === "cart",
  set: async () =>
    await navigateTo(
      { query: { ...route.query, sidebar: undefined } },
      { replace: true }
    ),
});
</script>

<template>
  <div class="min-h-dvh bg-surface-50 dark:bg-surface-800 flex flex-col">
    <AppHeader />

    <div class="container py-2 md:py-4 lg:py-8 flex-auto">
      <div class="grid lg:grid-cols-[256px_1fr] md:gap-6">
        <aside class="hidden lg:block" aria-label="Sidebar">
          <Menu
            :model="accountMenuItems"
            class="p-3 sticky top-8 border-none shadow"
          >
            <template #start>
              <h1 class="text-xl font-semibold whitespace-nowrap p-2">
                {{ $t("account.menu.myAccount") }}
              </h1>
            </template>

            <template #item="{ item }">
              <NuxtLink
                v-if="item.route"
                :to="formatLink(item.route)"
                class="flex items-center gap-3 p-2 rounded-lg"
              >
                <div v-if="item.icon" :class="item.icon" />
                <span>{{ item.label }}</span>
              </NuxtLink>

              <button
                v-else-if="item.command"
                class="flex items-center gap-3 rounded-lg p-2 w-full"
                @click="item.command"
              >
                <div v-if="item.icon" :class="item.icon" />
                <span>{{ item.label }}</span>
              </button>
            </template>
          </Menu>
        </aside>

        <main
          class="bg-white dark:bg-surface-900 p-4 rounded-lg shadow overflow-x-auto"
        >
          <slot />
        </main>
      </div>
    </div>

    <AppFooter />

    <Drawer
      v-model:visible="cartIsOpen"
      :header="$t('cart.header')"
      dismissable
      position="right"
    >
      <CartSidebar class="shadow-none" />
    </Drawer>
  </div>
</template>
